import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import DashboardLayout from "./DashboardLayout";
import { formatKolkataDateTime } from "../utils/dateTime";

const API = "/api";
const uploadsBase = "/uploads";

const pageStyle = { display: "grid", gap: "20px" };
const heroStyle = {
  background: "linear-gradient(135deg, #4e1b24 0%, #7c2d3a 48%, #c2410c 100%)",
  borderRadius: "28px",
  padding: "28px",
  color: "#ffffff",
  boxShadow: "0 28px 60px rgba(124, 45, 58, 0.20)",
};
const panelStyle = {
  background: "#ffffff",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  boxShadow: "0 18px 38px rgba(15, 23, 42, 0.08)",
};
const tableWrap = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thStyle = { textAlign: "left", padding: "14px 12px", color: "#7c2d3a", borderBottom: "1px solid rgba(148, 163, 184, 0.16)", fontSize: "13px", background: "rgba(255,247,237,0.72)" };
const tdStyle = { padding: "14px 12px", borderBottom: "1px solid rgba(148, 163, 184, 0.12)", color: "#0f172a", verticalAlign: "top" };

function ViewMoneyRequest() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/load-money-requests`, {
        params: { _ts: Date.now() },
      });
      setRows((Array.isArray(res.data) ? res.data : []).filter((row) => row.status === "approved"));
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  return (
    <DashboardLayout>
      <div style={pageStyle}>
        <section style={heroStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ maxWidth: "720px" }}>
              <p style={{ margin: 0, fontSize: "12px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.78 }}>
                Payment Workspace
              </p>
              <h1 style={{ margin: "10px 0 12px", fontSize: "34px", lineHeight: 1.1 }}>View Money Request</h1>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.84)", lineHeight: 1.75 }}>
                Approved load wallet requests are stored here for audit, receipt review, and franchise payment history.
              </p>
            </div>
            <div style={{ width: "92px", height: "92px", borderRadius: "26px", background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "38px" }}>
              <i className="bi bi-bank2"></i>
            </div>
          </div>
        </section>

        <section style={panelStyle}>
          <div style={{ marginBottom: "16px" }}>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Approved Requests</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>These load wallet requests were already approved and moved out from the request queue.</p>
          </div>

          <div style={tableWrap}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Franchise</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Reference</th>
                  <th style={thStyle}>Receipt</th>
                  <th style={thStyle}>Franchise Note</th>
                  <th style={thStyle}>Admin Note</th>
                  <th style={thStyle}>Approved At</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td style={tdStyle} colSpan={8}>Loading...</td></tr>
                ) : rows.length ? (
                  rows.map((row) => (
                    <tr key={row.id}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 700 }}>{row.centre_name || row.login_id || "-"}</div>
                        <div style={{ color: "#64748b", fontSize: "12px", marginTop: "4px" }}>{row.centre_code || row.contact || "-"}</div>
                      </td>
                      <td style={tdStyle}>Rs {Number(row.amount || 0).toFixed(2)}</td>
                      <td style={{ ...tdStyle, textTransform: "capitalize" }}>{String(row.payment_mode || "-").replaceAll("_", " ")}</td>
                      <td style={tdStyle}>{row.reference_no || "-"}</td>
                      <td style={tdStyle}>
                        {row.receipt_file ? (
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setReceiptPreview({ url: `${uploadsBase}/load-money-receipts/${row.receipt_file}` })}>
                            <i className="bi bi-eye-fill"></i>
                          </button>
                        ) : "-"}
                      </td>
                      <td style={tdStyle}>{row.note || "-"}</td>
                      <td style={tdStyle}>{row.admin_note || "-"}</td>
                      <td style={tdStyle}>{formatKolkataDateTime(row.approved_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td style={tdStyle} colSpan={8}>No approved money requests found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {receiptPreview
          ? createPortal(
              <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.58)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", zIndex: 99999 }} onClick={() => setReceiptPreview(null)}>
                <div style={{ background: "#fff", borderRadius: "22px", width: "min(640px, 100%)", maxHeight: "82vh", overflow: "auto", padding: "16px", boxShadow: "0 28px 80px rgba(15, 23, 42, 0.25)" }} onClick={(event) => event.stopPropagation()}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                    <h3 style={{ margin: 0, color: "#0f172a" }}>Receipt Preview</h3>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setReceiptPreview(null)}>Close</button>
                  </div>
                  {receiptPreview.url.toLowerCase().endsWith(".pdf") ? (
                    <iframe title="Receipt Preview" src={receiptPreview.url} style={{ width: "100%", height: "62vh", border: "none", borderRadius: "14px" }} />
                  ) : (
                    <img src={receiptPreview.url} alt="Receipt Preview" style={{ width: "100%", maxHeight: "62vh", objectFit: "contain", borderRadius: "14px" }} />
                  )}
                </div>
              </div>,
              document.body
            )
          : null}
      </div>
    </DashboardLayout>
  );
}

export default ViewMoneyRequest;
